import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { auditTime } from "rxjs/operators";
import { RealtimeService } from "../../../shared/realtime/realtime.service";
import { UserAppNotificationService } from "./user-app-notification.service";

const NOTIFICATION_CREATED_EVENT_NAME = "user.app-notification.created";

interface UserAppNotificationCreatedEvent {
    notificationId: number;
    occurredAt: string;
}

@Injectable({ providedIn: "root" })
export class UserAppNotificationRealtimeService {
    private readonly notificationService = inject(UserAppNotificationService);
    private readonly realtimeService = inject(RealtimeService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshRequested$ = new Subject<void>();
    private started = false;

    constructor() {
        this.refreshRequested$
            .pipe(auditTime(250), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.notificationService.notifyChanged());
    }

    ensureStarted(): void {
        if (this.started) {
            return;
        }
        this.started = true;
        const unsubscribe = this.realtimeService.subscribe(
            NOTIFICATION_CREATED_EVENT_NAME,
            (data) => this.handleMessage(data),
        );
        const readySubscription = this.realtimeService.ready$.subscribe(() =>
            this.refreshRequested$.next(),
        );
        this.destroyRef.onDestroy(() => {
            unsubscribe();
            readySubscription.unsubscribe();
        });
    }

    private handleMessage(data: unknown): void {
        if (this.parseEvent(data)) {
            this.refreshRequested$.next();
        }
    }

    private parseEvent(data: unknown): UserAppNotificationCreatedEvent | null {
        let parsed: unknown = data;
        if (typeof data === "string") {
            try {
                parsed = JSON.parse(data);
            } catch {
                return null;
            }
        }
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        const event = parsed as Record<string, unknown>;
        if (
            typeof event["notificationId"] !== "number" ||
            typeof event["occurredAt"] !== "string"
        ) {
            return null;
        }
        return {
            notificationId: event["notificationId"],
            occurredAt: event["occurredAt"],
        };
    }
}
